<script setup>
import { Carousel, Slide } from 'vue3-carousel'
import 'vue3-carousel/dist/carousel.css'

const props = defineProps({
  articles: Array,
  isVisible: Boolean,
  categories: Array,
  itemsToShow: {
    type: Number,
    default: 3,
  },
  breakpoints: {
    type: Object,
    default: () => {
      return {
        0: {
          itemsToShow: 1,
        },
        576: {
          itemsToShow: 2,
        },
        800: {
          itemsToShow: 3,
        },
        1024: {
          itemsToShow: 2,
        },
        1578: {
          itemsToShow: 3,
        },
        2160: {
          itemsToShow: 4,
        },
      }
    },
  },
  autoplay: {
    type: Number,
    default: 0,
  },
  wrapAround: {
    type: Boolean,
    default: false,
  },
})
const activeIndex = ref(0)
const carousel = ref(null)
const perPage = ref(20)
const mypage = ref(1)

const paginatedData = computed(() =>
  props.articles.slice((mypage.value - 1) * perPage.value, mypage.value * perPage.value),
)

function onHover(index) {
  activeIndex.value = index
}

function slidePrev() {
  carousel.value?.prev()
}

function slideNext() {
  carousel.value?.next()
}

const isMounted = ref(false)

onMounted(async () => {
  isMounted.value = true
})

useCarouselInert(carousel)
</script>

<template>
  <Carousel
    v-if="isVisible && isMounted"
    ref="carousel"
    class="carousel-article"
    :items-to-show="itemsToShow"
    :transition="500"
    :autoplay="autoplay"
    snap-align="start"
    :breakpoints="breakpoints"
    :wrap-around="wrapAround"
  >
    <Slide
      v-for="(item, index) in paginatedData"

      :key="item"
    >
      <CardArticleCard
        class="carousel__item"
        :article="item.content"
        :slug="item.full_slug"
        :active-index="activeIndex"
        :date="item.created_at"
        :index="index"
        :categories="categories"
        @mouseover="onHover(index)"
      />
    </Slide>
    <template #addons>
      <div class="carousel-article-navigation">
        <button
          class="carousel-article-button carousel-article-button-prev"
          type="button"
          aria-label="Previous article"
          @click="slidePrev"
        >
          <svg class="m-auto text-primary" width="22" height="22" viewBox="0 0 24 24">
            <path
              fill="currentColor"
              d="M16.62 2.99a1.25 1.25 0 0 0-1.77 0L6.54 11.3a.996.996 0 0 0 0 1.41l8.31 8.31c.49.49 1.28.49 1.77 0s.49-1.28 0-1.77L9.38 12l7.25-7.25c.48-.48.48-1.28-.01-1.76z"
            />
          </svg>
        </button>
        <button
          class="carousel-article-button carousel-article-button-next"
          type="button"
          aria-label="Next article"
          @click="slideNext"
        >
          <svg class="text-white" width="22" height="22" viewBox="0 0 24 24">
            <path
              fill="currentColor"
              d="M7.38 21.01c.49.49 1.28.49 1.77 0l8.31-8.31a.996.996 0 0 0 0-1.41L9.15 2.98c-.49-.49-1.28-.49-1.77 0s-.49 1.28 0 1.77L14.62 12l-7.25 7.25c-.48.48-.48 1.28.01 1.76z"
            />
          </svg>
        </button>
      </div>
    </template>
  </Carousel>

  <div
    v-else-if="isMounted && !isVisible"
    class="grid gap-4 3xl:grid-cols-4 lg:grid-cols-3 md:grid-cols-2 [&>_.skeleton-card:nth-child(1)]:hidden [&>_.skeleton-card:nth-child(2)]:hidden [&>_.skeleton-card:nth-child(3)]:hidden 3xl:[&>_.skeleton-card:nth-child(1)]:flex lg:[&>_.skeleton-card:nth-child(2)]:flex md:[&>_.skeleton-card:nth-child(3)]:flex"
  >
    <CardSkeletonCard v-for="item in 4" :key="item" />
  </div>
</template>

<style scoped>
.carousel-article .carousel__slide {
  @apply p-0;
}

.carousel-article .carousel__viewport {
  @apply py-6;
  contain: layout paint;
}

.carousel-article .carousel__track {
  backface-visibility: hidden;
  will-change: transform;
}

.carousel-article .carousel__slide {
  @apply rotate-y-0 scale-100;
  backface-visibility: hidden;
  contain: layout paint;
}

.carousel-article .carousel__slide--active ~ .carousel__slide {
  @apply scale-100 rotate-y-0 ;
}

.carousel-article .carousel__slide--prev {
  @apply scale-100 md:rotate-y-0 rotate-y-0 !important;
}

.carousel-article .carousel__slide--next {
  @apply scale-100 md:rotate-y-0 rotate-y-0  !important;
}

.carousel-article .carousel__slide--active {
  @apply scale-100 rotate-y-0 !important;
}

.carousel-article .carousel__item {
  @apply w-full rounded-md flex justify-center items-center my-[25px] mx-[15px];
}

.carousel-article {
  padding-bottom: 4.25rem;
}

.carousel-article-navigation {
  position: absolute;
  bottom: 0.25rem;
  left: 1.25rem;
  z-index: 20;
  display: flex;
  align-items: center;
  gap: 1rem;
}

.carousel-article-button {
  display: flex;
  height: 42px;
  width: 42px;
  cursor: pointer;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  transition:
    box-shadow 200ms ease,
    transform 200ms ease;
}

.carousel-article-button:hover {
  transform: translateY(-2px) scale(1.05);
}

.carousel-article-button-prev {
  background: rgba(244, 247, 249, 0.96);
  box-shadow: 0 0 0 6px rgba(244, 247, 249, 0.22);
}

.carousel-article-button-next {
  background: #ef3b3f;
  box-shadow: 0 0 0 6px rgba(239, 59, 63, 0.22);
}

@media (max-width: 767px) {
  .carousel-article {
    padding-bottom: 3.75rem;
  }

  .carousel-article .carousel__viewport {
    padding-block: 0.75rem 1rem;
  }

  .carousel-article .carousel__item {
    margin: 0.75rem 0.25rem;
  }

  .carousel-article-navigation {
    bottom: 0.25rem;
    left: 50%;
    gap: 0.85rem;
    transform: translateX(-50%);
  }

  .carousel-article-button {
    height: 36px;
    width: 36px;
  }

  .carousel-article-button-prev,
  .carousel-article-button-next {
    box-shadow: 0 0 0 5px rgba(244, 247, 249, 0.16);
  }
}

@media (min-width: 768px) and (max-width: 1279px) {
  .carousel-article {
    padding-bottom: 4rem;
  }

  .carousel-article .carousel__item {
    margin: 1rem 0.5rem;
  }

  .carousel-article-navigation {
    left: 50%;
    transform: translateX(-50%);
  }
}
</style>
